import { useState, useEffect, useCallback, useMemo } from 'react';
import { IResponsiveState, TResponsiveConfig, IBreakpointConfig } from '../types';

/**
 * 解析响应式配置
 * @param responsive 响应式配置对象
 * @returns 排序后的断点配置数组
 */
const parseResponsiveConfig = (responsive?: TResponsiveConfig): IBreakpointConfig[] => {
  if (!responsive) return [];
  
  return Object.entries(responsive)
    .map(([name, value]) => ({
      name,
      value: typeof value === 'string' ? parseInt((value as string).replace('px', ''), 10) : (value as number),
    }))
    .sort((a, b) => a.value - b.value);
};

/**
 * 获取当前匹配的断点
 * @param width 当前窗口宽度
 * @param breakpoints 断点配置数组
 * @returns 当前匹配的断点名称
 */
const getCurrentBreakpoint = (width: number, breakpoints: IBreakpointConfig[]): string => {
  let currentBreakpoint = 'xs';
  
  for (const breakpoint of breakpoints) {
    if (width >= breakpoint.value) {
      currentBreakpoint = breakpoint.name;
    } else {
      break;
    }
  }
  
  return currentBreakpoint;
};

/**
 * 获取当前断点对应的列数
 * @param currentBreakpoint 当前断点名称
 * @param responsive 响应式配置
 * @param defaultColumns 默认列数
 * @returns 当前应使用的列数
 */
const getCurrentColumns = (
  currentBreakpoint: string,
  responsive?: TResponsiveConfig,
  defaultColumns = 1
): number => {
  if (!responsive) return defaultColumns;
  
  // 从当前断点开始，向上查找匹配的配置
  const breakpoints = parseResponsiveConfig(responsive);
  const currentBreakpointIndex = breakpoints.findIndex(bp => bp.name === currentBreakpoint);
  
  if (currentBreakpointIndex === -1) return defaultColumns;
  
  // 找到当前断点或更小的断点配置
  for (let i = currentBreakpointIndex;i >= 0;i--) {
    const breakpoint = breakpoints[i];
    if (responsive[breakpoint.name] !== undefined) {
      return responsive[breakpoint.name];
    }
  }
  
  return defaultColumns;
};

/**
 * 响应式处理 Hook
 * @param colCount 默认列数
 * @param responsive 响应式配置
 * @returns 响应式状态
 */
export const useResponsive = (
  colCount: number,
  responsive?: TResponsiveConfig
): IResponsiveState => {
  // 始终调用 hooks，但根据条件决定是否监听窗口变化
  const [windowWidth, setWindowWidth] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  const handleResize = useCallback(() => {
    if (responsive) {
      setWindowWidth(window.innerWidth);
    }
  }, [responsive]);

  useEffect(() => {
    if (!responsive) return;
    
    // 添加防抖处理
    let timeoutId: NodeJS.Timeout;
    const debouncedHandleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };
    
    window.addEventListener('resize', debouncedHandleResize);
    
    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
      clearTimeout(timeoutId);
    };
  }, [handleResize, responsive]);

  // 使用 useMemo 计算响应式状态
  const responsiveState = useMemo((): IResponsiveState => {
    if (!responsive) {
      return {
        currentColumns: colCount,
        currentBreakpoint: null,
      };
    }

    const breakpoints = parseResponsiveConfig(responsive);
    const currentBreakpoint = getCurrentBreakpoint(windowWidth, breakpoints);
    const currentColumns = getCurrentColumns(currentBreakpoint, responsive, colCount);
    
    return {
      currentColumns,
      currentBreakpoint,
    };
  }, [windowWidth, colCount, responsive]);

  return responsiveState;
};
